import moment from "moment";

import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PendingApprovalSettingsContainerHandler extends BaseOperationHandler {
    private helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_ShowOptionsButtonClick, this.DisplayPendingApprovalSettings);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_CancelClick, this.SettingsContainerCancelClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_OkClick, this.SettingsContainerOkClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_ColumnOrderingPopup_Open, this.ColumnOrderingPopupOpen);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_ColumnOrderingPopup_Close, this.ColumnOrderingPopupClose);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchSettings_ClinicianFilterTextChanged, this.ClinicianFilterTextChanged);

        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.CloseSearchFilterPopup, false);
    }

    private CloseSearchFilterPopup(request: LocalEvent, store: PortfolioStore): void {
        if (store.PendingApprovalInboxModel.get("ShowPendingApprovalDisplaySettings") === true) {
            this.SettingsContainerOkClick(request, store);
            store.EmitChangeToListeners();
        }
    }

    private DisplayPendingApprovalSettings(request: LocalEvent, store: PortfolioStore): void {
        var open = store.PendingApprovalInboxModel.get("ShowPendingApprovalDisplaySettings");
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ShowPendingApprovalDisplaySettings", !open);
    }

    private SettingsContainerCancelClick(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ShowPendingApprovalDisplaySettings", false);
    }

    private SettingsContainerOkClick(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ShowPendingApprovalDisplaySettings", false);

        store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel);
    }

    private ColumnOrderingPopupOpen(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ColumnOrderingPopupOpen", true);

        sessionStore.showGlobalOverlay(true);
    }

    private ColumnOrderingPopupClose(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ColumnOrderingPopupOpen", false);

        sessionStore.hideGlobalOverlay();
    }

    private FromDateChanged(request: LocalEvent, store: PortfolioStore): void {
        let dateFrom = request.Get<Date>("value");
        dateFrom = dateFrom && moment(dateFrom).startOf("day").toDate();
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("DateFrom", dateFrom);
    }

    private ToDateChanged(request: LocalEvent, store: PortfolioStore): void {
        let dateTo = request.Get<Date>("value");
        dateTo = dateTo && moment(dateTo).endOf("day").toDate();
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("DateTo", dateTo);
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: PortfolioStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Portfolio_PendingApproval_Sites:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.PendingApprovalInboxModel.get("Sites"), id, checked));
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansFiltered", store.GetPendingApprovalCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Portfolio_PendingApproval_Clinicians:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.PendingApprovalInboxModel.get("CliniciansGlobalCache"), id, checked));
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansFiltered", store.GetPendingApprovalCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Portfolio_PendingApproval_SitesControlCheck:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.PendingApprovalInboxModel.get("Sites"), checked);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("Sites", sites);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansFiltered", store.GetPendingApprovalCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Portfolio_PendingApproval_CliniciansControlCheck:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectAllCliniciansChecked", checked);
                var list = store.GetPendingApprovalCliniciansBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.PendingApprovalInboxModel.get("CliniciansGlobalCache"), listOfClinicianIds, checked);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansGlobalCache", c);
                var checkedList = store.GetPendingApprovalCliniciansBasedOnSitesAndQuery();
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansFiltered", checkedList);
                break;
            case CheckboxTarget.Myle_Portfolio_PendingApproval_Status:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("Status", store.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.PendingApprovalInboxModel.get("Status"), id, checked));
                break;
            case CheckboxTarget.Myle_Portfolio_PendingApproval_StatusControlCheck:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectAllStatusChecked", checked);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("Status", this.helper.ChangeCheckStateFromAllICheckable(store.PendingApprovalInboxModel.get("Status"), checked));
                break;

            case CheckboxTarget.Myle_Portfolio_PendingApproval_OnlySelectedSites:
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ShowOnlySelectedSites", checked);
                break;

            default:
                break;
        }
    }

    private ClinicianFilterTextChanged(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansSearchText", request.Get<string>("value"));
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("CliniciansFiltered", store.GetPendingApprovalCliniciansBasedOnSitesAndQuery());
    }
}

export { PendingApprovalSettingsContainerHandler };
